import React, { InputHTMLAttributes, SyntheticEvent, useEffect, useRef, useState } from 'react';

import styled from 'styled-components';

const Label = styled.label`
  padding-bottom: 4px;
`;

const InputWrapper = styled.div<{ error: boolean; active: boolean }>`
  width: 100%;
  cursor: text;
  background: #fff !important;
  transition: all 0.2s ease-out;
  border: 2px solid
    ${(props) => {
      if (props.error) {
        return '#f44336';
      } else if (props.active) {
        return '#1976d2';
      }
      return 'rgba(0,0,0,0.12)';
    }};
  border-radius: 4px;
`;

const TextInput = styled.input`
  width: 100%;
  height: 100%;
  background: transparent !important;
  box-sizing: border-box;
  padding: 15px;
  border: 0;
  border-radius: 2px;
  outline: 0;
`;

const Primal = styled.div<{ error: boolean }>`
  opacity: 0.7;
  font-weight: 300;
  margin-bottom: 5px;
  font-size: 12px;
  padding-top: 18px;
  color: ${(props) => (props.error ? '#f44336' : '#3c4043')};
`;

const ErrorWrapper = styled.div`
  display: flex;
  padding-top: 8px;
`;

const Error = styled.div`
  color: #f44336;
  flex: 1 1 auto;
  font-size: 12px;
  font-weight: 400;
  min-height: 16px;
`;

interface Props extends InputHTMLAttributes<HTMLInputElement> {
  error?: string;
  label: string;
  onBlur?: () => void;
}

const InputField = ({ value, error, label, onBlur, ...rest }: Props) => {
  const [focused, setFocused] = useState(false);
  const input = useRef<HTMLLabelElement>(null);

  const onToggleFocus = (e: SyntheticEvent<any> | null, focused: boolean) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }

    setFocused(focused);

    if (focused) {
      input?.current?.focus();
    } else {
      if (onBlur) {
        onBlur();
      }
      input?.current?.blur();
    }
  };

  const clickHandler = (e: MouseEvent) => {
    if (focused && !input?.current?.contains(e.target as any)) {
      onToggleFocus(null, false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', clickHandler);
    return () => {
      document.removeEventListener('click', clickHandler);
    };
  }, []);

  return (
    <Label ref={input}>
      <Primal error={Boolean(error)}>{label}</Primal>
      <InputWrapper error={Boolean(error)} onClick={(e) => onToggleFocus(e, true)} active={focused}>
        <TextInput
          {...rest}
          value={value}
          onFocus={(e) => onToggleFocus(e, true)}
          onBlur={(e) => onToggleFocus(e, false)}
          onBlurCapture={(e) => onToggleFocus(e, false)}
        />
      </InputWrapper>
      <ErrorWrapper>
        <Error>{error}</Error>
      </ErrorWrapper>
    </Label>
  );
};

export default InputField;
